import { useMemo, type ReactNode } from 'react';
import { ApiProvider } from '@backstage/core-app-api';
import {
  configApiRef,
  useApi,
  useApiHolder,
  type ApiHolder,
  type ApiRef,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { commonTranslationRef } from '../../translations/features/common/common';

export type OrgSectionLabelSection =
  | 'components'
  | 'apis'
  | 'docs'
  | 'templates';

/**
 * O UserListPicker (usado nos filtros do Catálogo, APIs, Documentação e
 * Criar) exibe `organization.name` como rótulo da seção que contém o item
 * "Todos" e não expõe prop para customizá-lo. Como nesse contexto existem N
 * organizations, o nome da organization não faz sentido ali — a seção recebe
 * um rótulo contextual por página (`section`): Componentes no Catálogo, APIs,
 * Documentação e Templates. Este provider sobrescreve a leitura de
 * `organization.name` apenas dentro da subárvore que ele envolve.
 */
export const OrgSectionLabelProvider = ({
  section = 'components',
  children,
}: {
  section?: OrgSectionLabelSection;
  children: ReactNode;
}) => {
  const apiHolder = useApiHolder();
  const configApi = useApi(configApiRef);
  const { t } = useTranslationRef(commonTranslationRef);
  const label = t(`filterSection.${section}`);

  const scopedApis = useMemo<ApiHolder>(() => {
    const scopedConfig = new Proxy(configApi, {
      get(target, prop) {
        if (prop === 'getOptionalString') {
          return (key: string) =>
            key === 'organization.name' ? label : target.getOptionalString(key);
        }
        const value = (target as any)[prop];
        return typeof value === 'function' ? value.bind(target) : value;
      },
    });

    return {
      get<T>(ref: ApiRef<T>): T | undefined {
        if (ref.id === configApiRef.id) {
          return scopedConfig as unknown as T;
        }
        return apiHolder.get(ref);
      },
    };
  }, [apiHolder, configApi, label]);

  return <ApiProvider apis={scopedApis}>{children}</ApiProvider>;
};
